import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Read a cookie value by name (browser only). Returns null if not present. */
function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

/* ---------- JWT Bearer fallback (Safari/iPhone fix — iter42) -----------------
 * Safari ITP aggressively deletes 3rd-party cookies (SameSite=None), which
 * caused users on iPhone to be logged out on every refresh when the frontend
 * (myswimminglessons.fr) and the backend live on different domains.
 *
 * Mitigation: ALSO store the Bearer JWT returned by /auth/login. Use
 * localStorage when "Remember me" is checked (30-day persistence) and
 * sessionStorage otherwise (cleared on tab close). Sent as `Authorization:
 * Bearer <jwt>` header on every request as fallback when cookies are missing.
 * --------------------------------------------------------------------------- */
const JWT_KEY = "msl.jwt";

function readStoredJwt() {
  try {
    return localStorage.getItem(JWT_KEY) || sessionStorage.getItem(JWT_KEY) || null;
  } catch { return null; }
}

export function setJwt(token, remember = true) {
  try {
    if (!token) {
      localStorage.removeItem(JWT_KEY);
      sessionStorage.removeItem(JWT_KEY);
      return;
    }
    if (remember) {
      localStorage.setItem(JWT_KEY, token);
      sessionStorage.removeItem(JWT_KEY);
    } else {
      sessionStorage.setItem(JWT_KEY, token);
      localStorage.removeItem(JWT_KEY);
    }
  } catch (e) { /* storage unavailable in some private modes */ }
}

export function clearJwt() { setJwt(null); }

/* ---------- CSRF token store (cross-origin safe) ----------------------------
 * On cross-origin deployments (frontend on `myswimminglessons.fr`, backend on
 * `swim-reserve-app.preview.emergentagent.com`), the JS on the frontend cannot
 * read cookies set by the backend domain. The double-submit cookie pattern is
 * preserved server-side (cookie + DB row), but the FRONTEND keeps the token in
 * memory after login and refreshes it via GET /auth/csrf when missing or stale.
 * `sessionStorage` persists across refreshes within the same tab.
 * --------------------------------------------------------------------------- */
const CSRF_KEY = "msl.csrf";
let _csrfMem = null;

function readStoredCsrf() {
  if (_csrfMem) return _csrfMem;
  try {
    const v = sessionStorage.getItem(CSRF_KEY);
    if (v) _csrfMem = v;
    return v;
  } catch {
    return null;
  }
}

export function setCsrfToken(token) {
  _csrfMem = token || null;
  try {
    if (token) sessionStorage.setItem(CSRF_KEY, token);
    else sessionStorage.removeItem(CSRF_KEY);
  } catch (e) { /* sessionStorage unavailable in some private modes */ }
}

export function clearCsrfToken() { setCsrfToken(null); }

/** Best-effort: pick the freshest known token (memory > sessionStorage > cookie). */
function currentCsrf() {
  return readStoredCsrf() || getCookie("csrf_token");
}

/** Build the CSRF header object for raw `fetch()` calls (e.g. FormData uploads). */
export function csrfHeaders() {
  const t = currentCsrf();
  return t ? { "X-CSRF-Token": t } : {};
}

const instance = axios.create({
  baseURL: API,
  withCredentials: true, // always send session + csrf cookies (same-origin path)
});

let _refreshing = null;
async function refreshCsrf() {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      const r = await axios.get(`${API}/auth/csrf`, { withCredentials: true });
      const t = r?.data?.csrf_token;
      if (t) setCsrfToken(t);
      return t;
    } catch (_e) {
      return null;
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
}

// Inject Bearer JWT (Safari ITP fallback) + CSRF token header on state-changing requests.
instance.interceptors.request.use((cfg) => {
  // Safari/iPhone fix: when cookies are wiped by ITP, the Bearer JWT in
  // localStorage/sessionStorage keeps the session alive.
  if (!cfg.headers.Authorization) {
    const jwt = readStoredJwt();
    if (jwt) cfg.headers.Authorization = `Bearer ${jwt}`;
  }
  const method = (cfg.method || "get").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = currentCsrf();
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
});

// Retry once on CSRF-failed responses with a freshly fetched token.
instance.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const isCsrfFailure = status === 403 && detail === "CSRF check failed";
    if (isCsrfFailure && cfg && !cfg.__csrfRetried) {
      cfg.__csrfRetried = true;
      const fresh = await refreshCsrf();
      if (fresh) {
        cfg.headers["X-CSRF-Token"] = fresh;
        return instance.request(cfg);
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
